import type { NumericObservationsGroup } from '@ctw/shared/api/fhir/observations';
import type { Series } from '@ctw/shared/components/charts/multi-line-chart';
import type { LegacyTableColumn } from '@ctw/shared/components/tables/legacy-table';
import { Tooltip } from '@ctw/shared/components/tooltip';
import { formatFHIRDate } from '@ctw/shared/utils/dates';
import { tw } from '@ctw/shared/utils/tailwind';
import { faTriangleExclamation } from '@fortawesome/pro-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';

export type TrendsTableCellData = {
  value: string;
  referenceRange?: string;
  alertInterpretation?: string;
};

export type TrendsTableRow = {
  key: string;
  date: string;
  [seriesName: string]: TrendsTableCellData | null | string;
};

export const getDataAndColumnsForNumericObservationsByValueSet = (
  observations: NumericObservationsGroup,
): {
  tableData: Array<TrendsTableRow>;
  tableColumns: Array<LegacyTableColumn<TrendsTableRow>>;
  chartData: Array<Series>;
} => {
  if (!observations || observations.length === 0) {
    return { tableData: [], tableColumns: [], chartData: [] };
  }

  // Collect all unique dates from all series, newest first
  const allDates = Array.from(
    new Set(observations.flatMap((series) => series.data.map((d) => d.date))),
  ).sort((a, b) => (a < b ? 1 : -1));

  // Build table data: one row per date, columns for each series
  const tableData: Array<TrendsTableRow> = allDates.map((date) => {
    const row: TrendsTableRow = { key: date, date };
    for (const series of observations) {
      const point = series.data.find((d) => d.date === date);
      if (point) {
        row[series.displayName] = {
          value: point.valueDisplay,
          referenceRange: point.referenceRange,
          alertInterpretation: point.alertInterpretation,
        };
      } else {
        row[series.displayName] = null;
      }
    }
    return row;
  });

  // Build table columns: date + one per series
  const tableColumns: Array<LegacyTableColumn<TrendsTableRow>> = [
    {
      title: 'Date',
      render: (data) => formatFHIRDate(data?.key),
      widthPercent: 20,
    },
    ...observations.map((series) => ({
      title: series.displayName,
      widthPercent: Math.floor(80 / observations.length),
      render: (data: TrendsTableRow) => {
        const cellData = data[series.displayName];
        if (!cellData) {
          return null;
        }

        const { value, alertInterpretation, referenceRange } = cellData as TrendsTableCellData;

        return (
          <div className={tw`flex items-center whitespace-nowrap`}>
            {value}
            {alertInterpretation && (
              <Tooltip
                id={`alert-interpretation-${series.displayName}-${data.key}`}
                content={
                  referenceRange ? `Reference range: ${referenceRange}` : 'Alert interpretation'
                }
              >
                <span className={tw`ml-2 flex items-center gap-1 text-caution-text`}>
                  <FontAwesomeIcon icon={faTriangleExclamation} />
                  {alertInterpretation}
                </span>
              </Tooltip>
            )}
          </div>
        );
      },
    })),
  ];

  // Build chart data: transform observations into Series format for MultiLineChart
  const chartData: Array<Series> = observations.map((series) => ({
    seriesLabel: series.displayName,
    seriesData: series.data.map((point) => ({
      key: `${series.displayName}-${point.date}`,
      value: point.valueNumeric,
      date: point.date,
    })),
  }));

  return { tableData, tableColumns, chartData };
};
